import { IParsedEvent } from '@/Interfaces/Pixels/Event.interface';
import CollapseIcon from '@/components/Icons/CollapseIcon';
import ExpandIcon from '@/components/Icons/ExpandIcon';
import { PRIMARY } from '@/constants/color';
import { useAppDispatch } from '@/store/hooks';
import { downloadPixelReport } from '@/store/slices/pixelsThunks';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Box, Fade, IconButton, Popover, backdropClasses, popoverClasses, styled } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { BarElement, CategoryScale, ChartData, Chart as ChartJS, ChartOptions, LinearScale, Tooltip } from 'chart.js';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface CustomChartOptions extends ChartOptions<'bar'> {
  borderRadius?: Record<string, number> | number;
  borderSkipped?: boolean;
}

const StyledPopover = styled(Popover)({
  [`& .${popoverClasses.paper}`]: {
    width: '550px',
    padding: '16px 20px',
    borderRadius: '8px',
    backgroundColor: 'white',
    border: 'none',
    boxShadow: '0 10px 10px 0 rgb(16 10 85 / .1)',
  },
  [`& .${backdropClasses.root}`]: {
    backgroundColor: 'rgb(0 0 0 / .4)',
  },
});

const StyledIconButton = styled(IconButton)({
  border: '1px solid #E9E9E9',
  padding: '5px',
});

const previewChartOptions: CustomChartOptions = {
  plugins: {
    datalabels: {
      display: false,
    },
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
  },
  borderRadius: {
    topLeft: 2,
    topRight: 2,
  },
  borderSkipped: false,
  backgroundColor: 'rgb(27 67 50 / .23)',
  scales: {
    y: {
      grid: {
        display: false,
        drawTicks: false,
      },
      border: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
    x: {
      grid: {
        display: false,
        drawTicks: false,
      },
      border: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
  },
  maintainAspectRatio: false,
};

const popoverChartOptions: CustomChartOptions = {
  interaction: {
    intersect: false,
  },
  plugins: {
    datalabels: {
      display: false,
    },
    legend: {
      display: false,
    },
  },
  borderRadius: 4,
  borderSkipped: false,
  backgroundColor: PRIMARY,
  maintainAspectRatio: false,
};

const PixelFiredByDayCell = ({ value, row }: GridRenderCellParams<IParsedEvent, IParsedEvent['pixelsFiredByDay']>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const dispatch = useAppDispatch();

  const handleExpandClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleDownloadClick = useCallback(async () => {
    try {
      toast.info('Your pixel report is being processed in the background and will be downloaded once ready.');
      await dispatch(
        downloadPixelReport({ id: row.eventId, dateRangeStart: row.dateCreated, name: row.name }),
      ).unwrap();
    } catch (error) {
      console.error((error as Error)?.message);
      toast.error('Failed to generate full report for requested pixel.');
    }
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const id = useMemo(() => (open ? 'event-fires-graph' : undefined), [open]);

  const getChartData = useCallback(
    (categoryPercentage: number): ChartData<'bar'> => {
      const labels: string[] = [];
      const dataValues: number[] = [];
      value?.forEach(record => {
        labels.push(record.date);
        dataValues.push(record.value);
      });

      return {
        labels,
        datasets: [
          {
            data: dataValues,
            categoryPercentage,
          },
        ],
      };
    },
    [value],
  );

  const hasFires = useMemo(() => value?.some(v => v?.value), [value]);

  if (!hasFires) {
    return (
      <Box sx={{ position: 'relative' }}>
        <span>-</span>
        <StyledIconButton
          title='download'
          sx={{ padding: '4px', left: '16px', position: 'absolute' }}
          onClick={handleDownloadClick}
        >
          <FileDownloadOutlined sx={{ width: 16, height: 16 }} />
        </StyledIconButton>
      </Box>
    );
  }

  return (
    <>
      <Bar
        style={{
          maxWidth: '120px',
          maxHeight: '40px',
        }}
        options={previewChartOptions}
        data={getChartData(0.9)}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
        <StyledIconButton aria-describedby={id} title='expand' onClick={handleExpandClick}>
          <ExpandIcon />
        </StyledIconButton>
        <StyledIconButton title='download' sx={{ padding: '4px' }} onClick={handleDownloadClick}>
          <FileDownloadOutlined sx={{ width: 16, height: 16 }} />
        </StyledIconButton>
      </Box>
      <StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slots={{ transition: Fade }}
      >
        <div className='flex items-center justify-between mb-2'>
          <span className='font-semibold text-[18px]'>Pixel Fires By Day</span>
          <StyledIconButton title='collapse' sx={{ padding: '7px' }} onClick={handleClose}>
            <CollapseIcon />
          </StyledIconButton>
        </div>
        <div className='w-full h-[275px]'>
          <Bar options={popoverChartOptions} data={getChartData(0.7)} />
        </div>
      </StyledPopover>
    </>
  );
};

export default PixelFiredByDayCell;
